
// Simple AI content detection utility
// In a real implementation, this would call an API that uses more sophisticated detection

/**
 * Checks if the given text appears to be AI-generated
 * @param text The text to check
 * @returns Promise resolving to a result object with isAIGenerated flag
 */
export const checkAIContent = async (text: string): Promise<{ isAIGenerated: boolean, score: number }> => {
  // In a production app, this would call a backend service or API for AI detection
  // For demo purposes, we're implementing a basic heuristic check
  
  // Simple naive implementation for demonstration
  // In a real app, use a proper AI detection service
  
  if (!text || text.length < 100) {
    return { isAIGenerated: false, score: 0 };
  }
  
  // Some basic heuristics
  let aiScore = 0;
  
  // Check for unnaturally perfect structure
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
  const avgSentenceLength = text.length / sentences.length;
  
  // Unnaturally consistent sentence length
  const sentenceLengths = sentences.map(s => s.trim().length);
  const sentenceLengthVariance = calculateVariance(sentenceLengths);
  
  if (avgSentenceLength > 25 && sentenceLengthVariance < 100) {
    aiScore += 0.2;
  }
  
  // Check for complex vocabulary or formal language patterns
  const complexWords = ["furthermore", "additionally", "consequently", "therefore", "however", "nevertheless", "moreover"];
  const complexWordCount = complexWords.reduce((count, word) => {
    const regex = new RegExp(`\\b${word}\\b`, 'gi');
    return count + (text.match(regex)?.length || 0);
  }, 0);
  
  if (complexWordCount / sentences.length > 0.2) {
    aiScore += 0.3;
  }
  
  // Check for lack of personal pronouns (I, me, my, etc.) in descriptive/personal questions
  const personalPronouns = ["i", "me", "my", "mine", "myself"];
  const hasPersonalPronouns = personalPronouns.some(pronoun => {
    const regex = new RegExp(`\\b${pronoun}\\b`, 'i');
    return regex.test(text);
  });
  
  if (!hasPersonalPronouns && text.length > 200) {
    aiScore += 0.3;
  }
  
  // For demo purposes, check if content seems unusually polished
  const isVeryPolished = sentences.every(s => s.trim().length > 5 && /[,.;:]/.test(s));
  if (isVeryPolished) {
    aiScore += 0.2;
  }
  
  // Mimic some API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  return { 
    isAIGenerated: aiScore > 0.5,
    score: aiScore 
  };
};

// Helper function to calculate variance
function calculateVariance(array: number[]): number {
  const n = array.length;
  if (n === 0) return 0;
  
  const mean = array.reduce((a, b) => a + b, 0) / n;
  const variance = array.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / n;
  
  return variance;
}
